import React, { useState } from "react";
import { Button } from "../../controls";
import {
  ArrowDropDownRounded,
  DownloadRounded,
  PictureAsPdfRounded,
  PrintRounded,
  TableChartRounded,
} from "@mui/icons-material";
import { ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material";
import { API } from "../../api/api";
import {
  convertToUtc,
  downloadBlobFromResponse,
  toRangeEndUtc,
  toRangeStartUtc,
  ToFormData,
} from "../../hooks/Method";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

// Open a PDF blob in a hidden iframe and trigger the browser print dialog on it.
// Falls back to opening the PDF in a new tab if the iframe print is blocked.
const printPdfBlob = (blobData) => {
  const blob = new Blob([blobData], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  iframe.src = url;
  document.body.appendChild(iframe);
  iframe.onload = () => {
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } catch {
      window.open(url, "_blank");
    }
    // Keep iframe alive long enough for the print dialog to read it
    setTimeout(() => {
      document.body.removeChild(iframe);
      URL.revokeObjectURL(url);
    }, 60000);
  };
};

const FORMAT_LABELS = {
  excel: { labelKey: "excel", fallback: "Excel sheet", icon: TableChartRounded },
  pdf: { labelKey: "pdfDocument", fallback: "PDF document", icon: PictureAsPdfRounded },
  print: { labelKey: "print", fallback: "Print", icon: PrintRounded },
};

const Export = ({
  endpoint,
  pdfEndpoint,
  value,
  label,
  onExport,
  formats = ["excel"],
}) => {
  const { t } = useTranslation();
  const displayLabel = label ?? t("exportReport");
  const [loading, setLoading] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);

  // The export MUST cover exactly the same range as the table on screen, so it
  // uses the same day-boundary snapping (end day inclusive) rather than the raw
  // convertToUtc that silently dropped the final day.
  const formData = () =>
    ToFormData({
      ...value,
      fromDate: toRangeStartUtc(value.fromDate),
      toDate: toRangeEndUtc(value.toDate),
      today: convertToUtc(value.today),
    });

  const runExcel = () => {
    setLoading(true);
    if (onExport) {
      onExport(formData())
        .then((res) => downloadBlobFromResponse(res))
        .catch(() => toast.error(t("exportFailed", "Export failed")))
        .finally(() => setLoading(false));
      return;
    }
    API(endpoint).postFile(formData(), (res, err) => {
      setLoading(false);
      if (err || !res) return toast.error(t("exportFailed", "Export failed"));
      downloadBlobFromResponse(res);
    });
  };

  const runPdf = (mode = "download") => {
    if (!pdfEndpoint) {
      toast.error("PDF export not configured");
      return;
    }
    setLoading(true);
    API(pdfEndpoint).postFile(formData(), (res, err) => {
      setLoading(false);
      if (err || !res) return toast.error(t("exportFailed", "Export failed"));
      if (mode === "print") {
        printPdfBlob(res.data);
      } else {
        downloadBlobFromResponse(res);
      }
    });
  };

  const handleFormat = (fmt) => {
    setMenuAnchor(null);
    if (fmt === "excel") runExcel();
    else if (fmt === "pdf") runPdf("download");
    else if (fmt === "print") runPdf("print");
  };

  // Single-format mode: keep the original one-click behaviour.
  if (formats.length === 1) {
    const only = formats[0];
    return (
      <Button
        loading={loading}
        onClick={() => handleFormat(only)}
        // 40px = the small outlined inputs beside it (date range, search), so the
        // toolbar reads as one row.
        className="w-full md:w-auto !h-[40px]"
        variant="text"
        startIcon={<DownloadRounded />}
      >
        {displayLabel}
      </Button>
    );
  }

  // Multi-format mode: button opens a menu of available export formats.
  return (
    <>
      <Button
        loading={loading}
        onClick={(e) => setMenuAnchor(e.currentTarget)}
        // 40px = the small outlined inputs beside it (date range, search), so the
        // toolbar reads as one row.
        className="w-full md:w-auto !h-[40px]"
        variant="text"
        startIcon={<DownloadRounded />}
        endIcon={<ArrowDropDownRounded />}
      >
        {displayLabel}
      </Button>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {formats.map((fmt) => {
          const meta = FORMAT_LABELS[fmt];
          if (!meta) return null;
          const Icon = meta.icon;
          return (
            <MenuItem key={fmt} onClick={() => handleFormat(fmt)} sx={{ minWidth: 180 }}>
              <ListItemIcon>
                <Icon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t(meta.labelKey, meta.fallback)}</ListItemText>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export default Export;
